import type { User } from 'firebase/auth';
import type { UserProfile } from '@/core/types/index';

/**
 * @file src/core/store/auth.store.ts
 * @description Un store simple para manejar el estado de autenticación del usuario.
 * Utiliza el patrón observer para notificar a los suscriptores sobre los cambios.
 */

/**
 * Define la estructura del estado de autenticación.
 */
export interface AuthState {
  isLoggedIn: boolean;
  firebaseUser: User | null;
  user: UserProfile | null;
}

const initialState: AuthState = {
  isLoggedIn: false,
  firebaseUser: null,
  user: null,
};

/**
 * Crea una instancia del store de autenticación.
 * @returns El store de autenticación con sus métodos.
 */
function createAuthStore() {
  let state: AuthState = { ...initialState };

  const listeners = new Set<(state: AuthState) => void>();

  /**
   * Notifica a todos los suscriptores del estado actual.
   */
  const notify = () => {
    // Notifica a los suscriptores directos (módulos de TS)
    listeners.forEach(listener => listener(state));
    // Despacha un evento global para que componentes no basados en módulos puedan reaccionar.
    window.dispatchEvent(new CustomEvent<AuthState>('auth-state-update', { detail: state }));
  };

  return {
    /**
     * Obtiene el estado actual.
     * @returns {AuthState} El estado de autenticación.
     */
    getState(): AuthState {
      return state;
    },

    /**
     * Establece el usuario y actualiza el estado.
     * @param {UserProfile} userProfile El perfil de usuario de Firestore.
     * @param {User} firebaseUser El objeto de usuario de Firebase Auth.
     */
    setUser(userProfile: UserProfile, firebaseUser: User) {
      // Solo notificar si los datos realmente cambian para evitar re-renders innecesarios.
      if (JSON.stringify(state.user) !== JSON.stringify(userProfile)) {
        state = {
          isLoggedIn: true,
          user: userProfile,
          firebaseUser: firebaseUser,
        };
        notify();
      }
    },

    /**
     * Limpia los datos del usuario al cerrar sesión.
     */
    clearUser() {
      if (state.isLoggedIn) {
        state = { ...initialState };
        notify();
      }
    },

    /**
     * Se suscribe a los cambios del estado.
     * @param {(state: AuthState) => void} listener La función a ejecutar en cada cambio.
     * @returns {() => void} Una función para desuscribirse.
     */
    subscribe(listener: (state: AuthState) => void): () => void {
      listeners.add(listener);
      // Llama inmediatamente con el estado actual al suscribirse.
      listener(state);
      // Devuelve una función para que el componente pueda "limpiar" la suscripción al destruirse.
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

// Exportamos una única instancia del store (patrón Singleton).
export const authStore = createAuthStore();